import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatMessage } from '../api/conversations';

interface WebSocketMessageEvent {
  type: string;
  data?: any;
}

export function useConversationWs(conversationUuid?: string, enabled = true) {
  const queryClient = useQueryClient();
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const isMountedRef = useRef(true);

  const [isConnected, setIsConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const connectRef = useRef<() => void>(() => {});

  const cleanupSocket = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (socketRef.current) {
      // Detach listeners before closing to prevent triggering redundant onclose/reconnect loops
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onerror = null;
      socketRef.current.onclose = null;
      socketRef.current.close();
      socketRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!conversationUuid || !enabled || typeof window === 'undefined') return;

    const token = localStorage.getItem('access_token');
    if (!token) return;

    // Clean up existing socket before opening a new one
    cleanupSocket();

    const apiBase = import.meta.env.VITE_API_BASE_URL || '';
    const wsBase =
      import.meta.env.VITE_WS_BASE_URL ||
      (apiBase.startsWith('https://')
        ? apiBase.replace(/^https:\/\//, 'wss://')
        : apiBase.replace(/^http:\/\//, 'ws://'));

    const wsUrl = `${wsBase}/ws/conversations/${conversationUuid}/?token=${encodeURIComponent(token)}`;

    try {
      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) return;
        retryCountRef.current = 0; // Reset backoff on successful connection
        setIsConnected(true);
        setLastError(null);
      };

      ws.onmessage = (event) => {
        if (!isMountedRef.current) return;
        try {
          const payload: WebSocketMessageEvent = JSON.parse(event.data);
          console.log('[WS] Received event:', payload.type, payload);

          // Handle incoming messages
          const isNewMessage =
            payload.type === 'new_message' ||
            payload.type === 'chat_message' ||
            payload.type === 'chat.message' ||
            payload.type === 'message';

          const msgConversationUuid =
            conversationUuid ||
            payload.data?.conversation_id ||
            payload.data?.conversation_uuid ||
            payload.data?.conversation ||
            (payload as any).conversation_id ||
            (payload as any).conversation_uuid;

          if (isNewMessage && payload.data) {
            const incomingMsg: ChatMessage = payload.data;

            // Update React Query infinite cache for this conversation's messages
            if (msgConversationUuid) {
              queryClient.setQueryData<any>(
                ['conversation-messages', msgConversationUuid],
                (old: any) => {
                  if (!old) return old;
                  const hasMessage = old.pages?.some((page: any) => {
                    const results = Array.isArray(page)
                      ? page
                      : page?.messages || page?.data || page?.results || [];
                    return results.some((m: any) => m.id === incomingMsg.id);
                  });
                  if (hasMessage) return old;

                  const newPages = [...old.pages];
                  if (newPages.length > 0) {
                    const firstPage = newPages[0];
                    if (Array.isArray(firstPage)) {
                      newPages[0] = [incomingMsg, ...firstPage];
                    } else if (firstPage && Array.isArray(firstPage.messages)) {
                      newPages[0] = {
                        ...firstPage,
                        messages: [incomingMsg, ...firstPage.messages],
                      };
                    } else if (firstPage && Array.isArray(firstPage.data)) {
                      newPages[0] = {
                        ...firstPage,
                        data: [incomingMsg, ...firstPage.data],
                      };
                    } else if (firstPage && Array.isArray(firstPage.results)) {
                      newPages[0] = {
                        ...firstPage,
                        results: [incomingMsg, ...firstPage.results],
                      };
                    }
                  } else {
                    newPages[0] = {
                      messages: [incomingMsg],
                      next: null,
                      previous: null,
                    };
                  }

                  return {
                    ...old,
                    pages: newPages,
                  };
                },
              );
            }

            // Optimistically update conversations list item snippet & re-sort
            queryClient.setQueryData<any[]>(['conversations'], (old = []) => {
              let exists = false;
              const updated = old.map((conv) => {
                if (conv.conversation_uuid === msgConversationUuid) {
                  exists = true;
                  return {
                    ...conv,
                    last_message: incomingMsg,
                    last_message_text: incomingMsg.body,
                    last_message_time: incomingMsg.created_at,
                    updated_at: incomingMsg.created_at,
                  };
                }
                return conv;
              });

              if (!exists) {
                queryClient.invalidateQueries({ queryKey: ['conversations'] });
              }

              return [...updated].sort((a, b) => {
                const tA = new Date(a.updated_at || a.last_message_time || 0).getTime();
                const tB = new Date(b.updated_at || b.last_message_time || 0).getTime();
                return tB - tA;
              });
            });
          } else if (
            payload.type === 'message_read' ||
            payload.type === 'messages_read' ||
            payload.type === 'read_receipt' ||
            payload.type === 'conversation_read'
          ) {
            const readMsgId = payload.data?.message_id || payload.data?.id;
            if (msgConversationUuid) {
              queryClient.setQueryData<any>(
                ['conversation-messages', msgConversationUuid],
                (old: any) => {
                  if (!old) return old;
                  return {
                    ...old,
                    pages: old.pages.map((page: any) => {
                      const updateMsg = (m: any) => {
                        if (!readMsgId || m.id === readMsgId) {
                          return { ...m, is_read: true, read: true };
                        }
                        return m;
                      };

                      if (Array.isArray(page)) {
                        return page.map(updateMsg);
                      }
                      if (page && Array.isArray(page.results)) {
                        return {
                          ...page,
                          results: page.results.map(updateMsg),
                        };
                      }
                      return page;
                    }),
                  };
                },
              );
            }
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = () => {
        if (!isMountedRef.current) return;
        setLastError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        if (!isMountedRef.current) return;
        setIsConnected(false);
        socketRef.current = null;

        // Exponential backoff reconnection if closed unexpectedly
        if (enabled && !event.wasClean && retryCountRef.current < 10) {
          const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000);
          retryCountRef.current += 1;

          reconnectTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              connectRef.current?.();
            }
          }, delay);
        }
      };
    } catch (err: any) {
      if (isMountedRef.current) {
        setLastError(err.message || 'Failed to initialize WebSocket');
      }
    }
  }, [conversationUuid, enabled, queryClient, cleanupSocket]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    isMountedRef.current = true;
    connect();

    return () => {
      isMountedRef.current = false;
      cleanupSocket();
      setIsConnected(false);
    };
  }, [connect, cleanupSocket]);

  return {
    isConnected,
    lastError,
    reconnect: connect,
  };
}
